// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — MISE EN PLACE de la comptabilité et de la trésorerie (DÉMONSTRATION, plan non validé) :
//   • installation du plan comptable de démonstration (comptes, journaux, schémas fermés ; idempotente) ;
//   • exercices : ouverture (douze périodes ouvertes), clôture en deux temps (demande, puis confirmation par une
//     autre personne, toutes périodes closes) ;
//   • écriture manuelle (OD d'inventaire) : préparée en brouillon avec sa pièce, équilibre affiché, validée ensuite
//     par une autre personne ; abandon motivé d'un brouillon ; jamais de modification d'une écriture validée ;
//   • compte de trésorerie de démonstration (banque ou caisse).
// Routes : README finance § 5 (plan-demo, exercices, ecritures, comptes-tresorerie) ; tout POST avec Idempotency-Key.
// Composants définis hors de tout parent (react-component-rules, règle 1).
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookPlus, CalendarPlus, CheckCircle2, Landmark, Lock, Plus, Trash2 } from 'lucide-react';
import { Badge, Bouton, BandeauInfo, EtatVide } from '../../design/composants';
import { toArray, aujourdhuiDakar } from './api';
import { Champ, Etat } from './commun';
import { francs, lireMontant } from './apiSocial';
import { FenetreActe, Montant, TitreSection, usePieceDepot, ChampPiece, ChampMotif, motifValide } from './communSocial';
import { useMiseEnPlace, ETATS_EXERCICE } from './apiMiseEnPlace';

/** Mention obligatoire de chaque acte de mise en place : rien n'a de valeur comptable. */
export function MentionPlanNonValide() {
  return (
    <BandeauInfo ton="alerte" titre="Plan non validé par l'expert-comptable">
      Plan comptable de démonstration : comptes, journaux et écritures servent à la recette et n'ont aucune valeur comptable.
    </BandeauInfo>
  );
}

// ─── Plan de démonstration ─────────────────────────────────────────────────────────────────────
function FenetreInstallerPlan({ organisation, onFermer, onFait }) {
  const api = useMiseEnPlace();
  return (
    <FenetreActe titre="Installer le plan de démonstration" Icone={BookPlus} libelleBouton="Installer le plan" onFermer={onFermer}
      aide="Crée les comptes, les journaux (banque, caisse, paie, opérations diverses) et les schémas d'écriture de démonstration de l'organisation. L'opération peut être relancée sans rien dupliquer."
      onConfirmer={async (cle) => {
        const r = await api.installerPlanDemo(organisation.id, cle);
        onFait(`Plan de démonstration installé : ${r?.comptes ?? '—'} compte(s) dont ${r?.comptes_crees ?? 0} créé(s), ${r?.journaux ?? '—'} journaux, ${r?.schemas ?? '—'} schémas.`);
      }}>
      <MentionPlanNonValide />
    </FenetreActe>
  );
}

// ─── Exercices ─────────────────────────────────────────────────────────────────────────────────
/** Exercices déduits des périodes du référentiel : année, état, périodes non closes. */
export function exercicesDe(periodes) {
  const parAnnee = new Map();
  toArray(periodes).forEach((p) => {
    const e = parAnnee.get(p.annee) || { annee: p.annee, etat: p.etat_exercice, periodes: [] };
    e.periodes.push(p);
    parAnnee.set(p.annee, e);
  });
  return [...parAnnee.values()].sort((a, b) => b.annee - a.annee).map((e) => ({ ...e, nonCloses: e.periodes.filter((p) => p.etat !== 'CLOSE').length }));
}

function FenetreOuvrirExercice({ organisation, existantes, onFermer, onFait }) {
  const api = useMiseEnPlace();
  const courante = Number(aujourdhuiDakar().slice(0, 4));
  const [annee, setAnnee] = useState(String(existantes.includes(courante) ? Math.max(...existantes) + 1 : courante));
  const a = /^\d{4}$/.test(annee.trim()) ? Number(annee.trim()) : null;
  const valide = a !== null && a >= 2020 && a <= 2100;
  return (
    <FenetreActe titre="Ouvrir un exercice" Icone={CalendarPlus} libelleBouton="Ouvrir l'exercice" pret={valide && !existantes.includes(a)} onFermer={onFermer}
      aide="L'exercice commence au mois de début d'exercice de l'organisation ; ses douze périodes sont ouvertes aux écritures."
      onConfirmer={async (cle) => { await api.ouvrirExercice(organisation.id, a, cle); onFait(`Exercice ${a} ouvert : douze périodes ouvertes.`); }}>
      <Champ libelle="Année de début" requis aide={a && existantes.includes(a) ? 'Cet exercice est déjà ouvert.' : 'Entre 2020 et 2100.'}>
        <input className="rh-champ-saisie" inputMode="numeric" maxLength={4} value={annee} onChange={(e) => setAnnee(e.target.value)} />
      </Champ>
    </FenetreActe>
  );
}

export function FenetreClotureExercice({ organisation, exercice, etape, onFermer, onFait }) {
  const api = useMiseEnPlace();
  if (etape === 'DEMANDER') {
    return (
      <FenetreActe titre={`Demander la clôture de l'exercice ${exercice.annee}`} Icone={Lock} libelleBouton="Demander la clôture" onFermer={onFermer}
        aide="Une autre personne confirmera ensuite la clôture, une fois toutes les périodes closes. La clôture fixe l'empreinte des journaux de l'exercice."
        onConfirmer={async (cle) => { await api.cloturerExercice(organisation.id, exercice.annee, 'DEMANDER', cle); onFait(`Clôture de l'exercice ${exercice.annee} demandée.`); }}>
        {exercice.nonCloses > 0 && <p style={{ margin: 0, fontSize: 14 }}>{exercice.nonCloses} période(s) ne sont pas encore closes : la confirmation ne sera possible qu'après leur clôture.</p>}
      </FenetreActe>
    );
  }
  return (
    <FenetreActe titre={`Confirmer la clôture de l'exercice ${exercice.annee}`} Icone={Lock} libelleBouton="Confirmer la clôture" danger onFermer={onFermer}
      aide="La personne qui a demandé la clôture ne la confirme pas. Toutes les périodes doivent être closes ; la clôture est définitive."
      onConfirmer={async (cle) => {
        const r = await api.cloturerExercice(organisation.id, exercice.annee, 'CONFIRMER', cle);
        onFait(`Exercice ${exercice.annee} clos${r?.empreinte_cloture ? ` (empreinte ${String(r.empreinte_cloture).slice(0, 16)}…)` : ''}.`);
      }}>
      {exercice.nonCloses > 0 && <BandeauInfo ton="alerte">{exercice.nonCloses} période(s) ne sont pas closes : le serveur refusera la confirmation.</BandeauInfo>}
    </FenetreActe>
  );
}

function CarteExercices({ organisation, droits, referentiel, onFenetre }) {
  const exercices = exercicesDe(referentiel.donnees?.periodes);
  const planInstalle = toArray(referentiel.donnees?.balance).length > 0;
  return (
    <section className="rh-carte">
      <TitreSection action={droits.a('ds_ecriture_saisir') && planInstalle
        ? <Bouton petit Icone={CalendarPlus} onClick={() => onFenetre({ type: 'exercice', existantes: exercices.map((e) => e.annee) })}>Ouvrir un exercice</Bouton> : null}>
        2. Exercices
      </TitreSection>
      {!planInstalle && <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Installez d'abord le plan de démonstration.</p>}
      {planInstalle && !exercices.length && <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Aucun exercice ouvert : aucune écriture ne peut encore être passée.</p>}
      {exercices.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {exercices.map((e) => (
            <li key={e.annee} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px 0', borderBottom: '1px solid var(--sg-bord)' }}>
              <span style={{ fontSize: 14 }}>
                <strong style={{ fontWeight: 500 }}>Exercice {e.annee}</strong> <Etat table={ETATS_EXERCICE} code={e.etat} />
                <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{e.periodes.length - e.nonCloses} période(s) close(s) sur {e.periodes.length}</span>
              </span>
              <span style={{ display: 'flex', gap: 6 }}>
                {e.etat === 'OUVERT' && droits.a('ds_periode_cloturer_demander') && <Bouton petit Icone={Lock} onClick={() => onFenetre({ type: 'cloture', exercice: e, etape: 'DEMANDER' })}>Demander la clôture</Bouton>}
                {e.etat === 'CLOTURE_DEMANDEE' && droits.a('ds_periode_cloturer_confirmer') && <Bouton petit variante="primaire" Icone={Lock} onClick={() => onFenetre({ type: 'cloture', exercice: e, etape: 'CONFIRMER' })}>Confirmer la clôture</Bouton>}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="rh-form-aide" style={{ margin: '8px 0 0' }}>La clôture mois par mois se fait dans « Exercices et périodes ».</p>
    </section>
  );
}

/** Vue « Mise en place » de l'onglet Comptabilité : plan, exercices, trésorerie. */
export function MiseEnPlaceCompta({ organisation, droits, referentiel }) {
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const d = referentiel.donnees;
  const comptes = toArray(d?.balance);
  const journaux = toArray(d?.journaux);
  const installe = comptes.length > 0;
  const fait = (m) => { setFenetre(null); setInfo(m); referentiel.recharger(); };
  if (referentiel.erreur || !d) return null;
  return (
    <>
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      <section className="rh-carte">
        <TitreSection action={droits.a('ds_ecriture_saisir')
          ? <Bouton petit variante={installe ? 'secondaire' : 'primaire'} Icone={BookPlus} onClick={() => setFenetre({ type: 'plan' })}>{installe ? 'Compléter le plan' : 'Installer le plan de démonstration'}</Bouton> : null}>
          1. Plan comptable de démonstration
        </TitreSection>
        {installe ? (
          <p style={{ margin: 0, fontSize: 14 }}>
            <Badge ton="alerte">Démonstration</Badge> {comptes.length} compte(s), {journaux.length} journal(aux) installés. Plan non validé par l'expert-comptable.
          </p>
        ) : (
          <EtatVide Icone={BookPlus} titre="Plan non installé"
            texte={droits.a('ds_ecriture_saisir') ? 'Installez le plan de démonstration pour ouvrir un exercice, créer les comptes de trésorerie et passer des écritures.'
              : 'Le plan de démonstration est installé par le trésorier (habilitation « saisir une écriture »).'} />
        )}
      </section>
      <CarteExercices organisation={organisation} droits={droits} referentiel={referentiel} onFenetre={setFenetre} />
      <section className="rh-carte">
        <TitreSection>3. Comptes de trésorerie et chéquiers</TitreSection>
        <p style={{ margin: 0, fontSize: 14 }}>
          Les comptes (banque, caisse) se créent dans <Link className="rh-lien" to={`/dispositifs/${organisation.id}/tresorerie?vue=comptes`}>Trésorerie › Comptes et caisse</Link>,
          {' '}après l'installation du plan ; les chéquiers s'enregistrent depuis le bouton « Chèques » d'un compte bancaire.
        </p>
      </section>
      {fenetre?.type === 'plan' && <FenetreInstallerPlan organisation={organisation} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'exercice' && <FenetreOuvrirExercice organisation={organisation} existantes={fenetre.existantes} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'cloture' && <FenetreClotureExercice organisation={organisation} exercice={fenetre.exercice} etape={fenetre.etape} onFermer={() => setFenetre(null)} onFait={fait} />}
    </>
  );
}

// ─── Écriture manuelle (OD d'inventaire) ───────────────────────────────────────────────────────
const nouvelleLigne = (n) => ({ cle: `l${Date.now()}-${n}`, compte_id: '', sens: n % 2 ? 'credit' : 'debit', montant: '', libelle: '' });

function LigneSaisie({ ligne, rang, comptes, peutRetirer, onChange, onRetirer }) {
  return (
    <div className="ds-ligne-ecriture" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, alignItems: 'center' }}>
      <select className="rh-champ-saisie" style={{ flex: '1 1 240px', minWidth: 0, width: 'auto' }} aria-label={`Compte de la ligne ${rang}`} value={ligne.compte_id} onChange={(e) => onChange({ ...ligne, compte_id: e.target.value })}>
        <option value="">Compte…</option>
        {comptes.map((c) => <option key={c.id} value={c.id}>{c.numero} — {c.libelle}</option>)}
      </select>
      <select className="rh-champ-saisie" style={{ flex: '0 0 104px', width: 104 }} aria-label={`Sens de la ligne ${rang}`} value={ligne.sens} onChange={(e) => onChange({ ...ligne, sens: e.target.value })}>
        <option value="debit">Débit</option><option value="credit">Crédit</option>
      </select>
      <input className="rh-champ-saisie" style={{ flex: '1 1 110px', minWidth: 0, width: 'auto' }} aria-label={`Montant de la ligne ${rang}`} inputMode="numeric" placeholder="Montant" value={ligne.montant} onChange={(e) => onChange({ ...ligne, montant: e.target.value })} />
      <Bouton petit variante="discret" Icone={Trash2} aria-label={`Retirer la ligne ${rang}`} disabled={!peutRetirer} onClick={onRetirer} />
    </div>
  );
}

/** Totaux et équilibre de lignes saisies : { debit, credit, ecart, completes }. */
export function equilibre(lignes) {
  const debit = lignes.reduce((s, l) => s + (l.sens === 'debit' ? lireMontant(l.montant) || 0 : 0), 0);
  const credit = lignes.reduce((s, l) => s + (l.sens === 'credit' ? lireMontant(l.montant) || 0 : 0), 0);
  const completes = lignes.length >= 2 && lignes.every((l) => l.compte_id && lireMontant(l.montant) > 0);
  return { debit, credit, ecart: debit - credit, completes };
}

export function FenetreEcritureManuelle({ organisation, comptes, onFermer, onFait }) {
  const api = useMiseEnPlace();
  const piece = usePieceDepot(organisation.id, 'ECRITURE', null, 'JUSTIFICATIF');
  const [lib, setLib] = useState('');
  const [date, setDate] = useState(aujourdhuiDakar());
  const [lignes, setLignes] = useState(() => [nouvelleLigne(0), nouvelleLigne(1)]);
  const eq = equilibre(lignes);
  const equilibree = eq.debit > 0 && eq.ecart === 0;
  const pret = !!(lib.trim().length >= 3 && date && eq.completes && equilibree && piece.prete && lignes.length <= 50);
  const changer = (i, l) => setLignes((ls) => ls.map((x, j) => (j === i ? l : x)));
  return (
    <FenetreActe titre="Préparer une écriture manuelle" Icone={BookPlus} libelleBouton="Préparer le brouillon" pret={pret} onFermer={onFermer} large
      aide="Écriture d'opérations diverses (inventaire). Elle reste en brouillon, sans numéro, jusqu'à sa validation par une autre personne ; une écriture validée ne se modifie plus (correction par contrepassation)."
      onConfirmer={async (cle) => {
        const p = await piece.deposer();
        const r = await api.saisirEcriture(organisation.id, {
          libelle: lib.trim(), date, piece_id: p.id,
          lignes: lignes.map((l) => ({ compte_id: l.compte_id, [l.sens]: lireMontant(l.montant) })),
        }, cle);
        onFait(`Écriture préparée en brouillon${r?.periode ? ` sur la période ${r.periode}` : ''}${r?.tardive ? ' (période d\'origine close : écriture tardive)' : ''}. Elle attend la validation d'une autre personne.`);
      }}>
      <MentionPlanNonValide />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <Champ libelle="Libellé" requis><input className="rh-champ-saisie" maxLength={200} value={lib} onChange={(e) => setLib(e.target.value)} /></Champ>
        <Champ libelle="Date de l'opération" requis><input type="date" className="rh-champ-saisie" value={date} onChange={(e) => setDate(e.target.value)} /></Champ>
      </div>
      <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 500, color: 'var(--sg-texte-2)' }}>Lignes (un débit ou un crédit par ligne)</p>
      {!comptes.length && <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>Aucun compte : installez le plan de démonstration.</p>}
      {lignes.map((l, i) => (
        <LigneSaisie key={l.cle} ligne={l} rang={i + 1} comptes={comptes} peutRetirer={lignes.length > 2} onChange={(x) => changer(i, x)} onRetirer={() => setLignes((ls) => ls.filter((_, j) => j !== i))} />
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '4px 0 12px' }}>
        <Bouton petit Icone={Plus} disabled={lignes.length >= 50} onClick={() => setLignes((ls) => [...ls, nouvelleLigne(ls.length)])}>Ajouter une ligne</Bouton>
        <div data-equilibre={equilibree ? 'oui' : 'non'} style={{ fontSize: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span>Débit <Montant n={eq.debit} fort /></span><span>Crédit <Montant n={eq.credit} fort /></span>
          {equilibree ? <Badge ton="succes" point>Équilibrée</Badge> : <Badge ton="danger" point>{eq.debit || eq.credit ? `Écart ${francs(Math.abs(eq.ecart))}` : 'À saisir'}</Badge>}
        </div>
      </div>
      <ChampPiece piece={piece} types={['JUSTIFICATIF', 'FACTURE', 'PV', 'DECISION', 'AUTRE']} libelleChamp="Pièce justificative (obligatoire)" />
    </FenetreActe>
  );
}

export function FenetreAbandonnerEcriture({ ecriture, onFermer, onFait, abandonner }) {
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre={`Abandonner le brouillon « ${ecriture.libelle} »`} Icone={Trash2} libelleBouton="Abandonner le brouillon" danger pret={motifValide(motif)} onFermer={onFermer}
      aide="Un brouillon n'a pas de numéro : son abandon est conservé dans l'historique, rien n'est effacé."
      onConfirmer={async (cle) => { await abandonner(ecriture.id, motif.trim(), cle); onFait('Brouillon abandonné.'); }}>
      <ChampMotif valeur={motif} onChange={setMotif} />
    </FenetreActe>
  );
}

/** Validation d'une écriture par une 2e personne ; pièce exigée si le brouillon n'en porte pas. */
export function FenetreValiderAvecPiece({ organisation, ecriture, onFermer, onFait, children }) {
  const api = useMiseEnPlace();
  const sansPiece = !ecriture.piece_id;
  const piece = usePieceDepot(organisation.id, 'ECRITURE', ecriture.id, 'JUSTIFICATIF');
  return (
    <FenetreActe titre={`Valider l'écriture « ${ecriture.libelle} »`} Icone={CheckCircle2} libelleBouton="Valider" pret={!sansPiece || piece.prete} onFermer={onFermer}
      aide="L'auteur d'une écriture ne la valide pas. La validation attribue le numéro sans trou et chaîne l'empreinte ; l'écriture est ensuite figée."
      onConfirmer={async (cle) => {
        const p = sansPiece ? await piece.deposer() : null;
        const r = await api.validerEcriture(ecriture.id, p ? p.id : null, cle);
        onFait(`Écriture validée${r?.numero ? ` sous le numéro ${r.numero}` : ''}.`);
      }}>
      {children}
      {sansPiece && <ChampPiece piece={piece} types={['JUSTIFICATIF', 'FACTURE', 'PV', 'DECISION', 'AUTRE']} libelleChamp="Pièce justificative (obligatoire pour valider)" />}
    </FenetreActe>
  );
}

// ─── Compte de trésorerie ──────────────────────────────────────────────────────────────────────
export function FenetreCompteTresorerie({ organisation, onFermer, onFait }) {
  const api = useMiseEnPlace();
  const [type, setType] = useState('BANQUE');
  const [lib, setLib] = useState('');
  const [banque, setBanque] = useState('');
  const [rib, setRib] = useState('');
  const banqueOk = type === 'CAISSE' || banque.trim().length >= 2;
  const ribOk = !rib.trim() || /^[*X0-9 ]{4,40}$/.test(rib.trim());
  return (
    <FenetreActe titre="Créer un compte de trésorerie" Icone={Landmark} libelleBouton="Créer le compte" pret={lib.trim().length >= 3 && banqueOk && ribOk} onFermer={onFermer}
      aide="Compte de démonstration : son libellé portera la marque [DÉMO]. Le plan de démonstration doit être installé."
      onConfirmer={async (cle) => {
        const r = await api.creerCompteTresorerie(organisation.id, {
          type, libelle: lib.trim(), ...(type === 'BANQUE' ? { banque: banque.trim() } : {}), ...(type === 'BANQUE' && rib.trim() ? { rib_masque: rib.trim() } : {}),
        }, cle);
        onFait(`Compte « ${r?.libelle || lib.trim()} » créé.`);
      }}>
      <Champ libelle="Type" requis>
        <select className="rh-champ-saisie" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="BANQUE">Banque</option><option value="CAISSE">Caisse</option>
        </select>
      </Champ>
      <Champ libelle="Libellé" requis aide="3 caractères au moins."><input className="rh-champ-saisie" maxLength={110} value={lib} onChange={(e) => setLib(e.target.value)} /></Champ>
      {type === 'BANQUE' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          <Champ libelle="Banque" requis><input className="rh-champ-saisie" maxLength={80} value={banque} onChange={(e) => setBanque(e.target.value)} /></Champ>
          <Champ libelle="RIB masqué" aide={ribOk ? 'Facultatif. Jamais le RIB complet : chiffres, X ou *.' : 'Chiffres, X ou * seulement (4 à 40).'}>
            <input className="rh-champ-saisie" maxLength={40} placeholder="XXXX XXXX 1234" value={rib} onChange={(e) => setRib(e.target.value)} />
          </Champ>
        </div>
      )}
    </FenetreActe>
  );
}
